// ConsentForm.jsx
// Shared SMS consent capture component. Rendered from a single entry
// point: the standalone "SMS Notifications" nav tab, available to any
// signed-in user.
import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useOrgDirectory } from "./OrgDirectoryContext";
import { loginRequest } from "./authConfig";
import ConsentFormBody from "./ConsentFormBody";

export default function ConsentForm() {
  const { instance, accounts } = useMsal();
  const { orgUsers } = useOrgDirectory();
  const account = accounts && accounts[0];
  const me = account
    ? orgUsers.find(function (u) { return u.mail && account.username && u.mail.toLowerCase() === account.username.toLowerCase(); })
    : null;

  const [name, setName] = useState((me && me.displayName) || (account && account.name) || "");
  const [email, setEmail] = useState((me && me.mail) || (account && account.username) || "");
  const [phone, setPhone] = useState((me && me.phone) || "");
  const [agreed, setAgreed] = useState(false);
  const [status, setStatus] = useState(null); // null | "incomplete" | "saving" | "saved" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  // Error 30923: opt-in must be genuinely optional -- the phone field and
  // checkbox no longer hard-block submission (no `required`, button is
  // never disabled). An incomplete submission is rejected here with a
  // plain inline message instead, and the request is never sent -- it's
  // also rejected server-side (isValidConsentSubmission) as defense in
  // depth, but there's no reason to round-trip an obviously-incomplete one.
  async function handleSubmit(e) {
    e.preventDefault();
    if (!account) return;
    if (!agreed || !phone) {
      setStatus("incomplete");
      return;
    }
    setStatus("saving");
    try {
      const tokenResp = await instance.acquireTokenSilent({ ...loginRequest, account: account });
      const userId = (me && me.id) || account.localAccountId;
      const res = await fetch("/api/notify/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + tokenResp.accessToken },
        body: JSON.stringify({ userId: userId, name: name, email: email, phoneNumberAtConsent: phone, agreed: agreed }),
      });
      if (!res.ok) {
        const err = await res.json().catch(function () { return {}; });
        throw new Error(err.error || ("Request failed: " + res.status));
      }
      setStatus("saved");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message);
    }
  }

  return (
    <ConsentFormBody
      name={name} setName={setName}
      email={email} setEmail={setEmail}
      phone={phone} setPhone={setPhone}
      agreed={agreed} setAgreed={setAgreed}
      onSubmit={handleSubmit}
      submitDisabled={status === "saving"}
      submitLabel={status === "saving" ? "Saving..." : "Submit Consent"}
      statusNode={
        <>
          {status === "incomplete" && <div style={{ marginTop: 10, color: "#6b7280", fontSize: 12 }}>A phone number and the checkbox above are both required to opt in to SMS. Nothing was submitted -- you can still leave this tab any time.</div>}
          {status === "saved" && <div style={{ marginTop: 10, color: "#27ae60", fontSize: 12 }}>Consent recorded.</div>}
          {status === "error" && <div style={{ marginTop: 10, color: "#c0392b", fontSize: 12 }}>Error: {errorMsg}</div>}
        </>
      }
    />
  );
}
